/**
 * useNotifications — data hook for the NotificationBell in the top nav.
 *
 * MV5 Sprint 2.C. Fetches the paginated feed + unread count from
 * session-manager, then polls every 30s. When the WS gateway ships the
 * `/ws/notifications/stream` socket (Wave 2), replace the polling loop
 * with an event subscription — the hook API stays stable.
 */

import { useCallback, useEffect, useState } from 'react';
import api from '../services/api';

export interface Notification {
  id: string;
  kind: string;
  payload: Record<string, unknown>;
  deliveredChannels: Record<string, boolean>;
  readAt: string | null;
  createdAt: string;
}

const POLL_MS = 30_000;

export function useNotifications() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.get('/notifications', {
        params: { limit: 25 },
      });
      setNotifications(
        (resp.data.notifications ?? []).map((n: any) => ({
          id: n.id,
          kind: n.kind,
          payload: n.payload ?? {},
          deliveredChannels: n.deliveredChannels ?? {},
          readAt: n.readAt,
          createdAt: n.createdAt,
        })),
      );
      setUnreadCount(resp.data.unread_count ?? 0);
      setError(null);
    } catch (err: any) {
      setError(err?.message || 'Failed to load notifications.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  const markRead = useCallback(async (id: string) => {
    // Optimistic update.
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, readAt: new Date().toISOString() } : n)),
    );
    setUnreadCount((c) => Math.max(0, c - 1));
    try {
      await api.patch(`/notifications/${id}/read`);
    } catch {
      // Refetch on failure to reconcile.
      void load();
    }
  }, [load]);

  const markAllRead = useCallback(async () => {
    const prevUnread = unreadCount;
    setNotifications((prev) =>
      prev.map((n) => (n.readAt ? n : { ...n, readAt: new Date().toISOString() })),
    );
    setUnreadCount(0);
    try {
      await api.post('/notifications/mark-all-read');
    } catch {
      setUnreadCount(prevUnread);
      void load();
    }
  }, [load, unreadCount]);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    reload: load,
    markRead,
    markAllRead,
  };
}
